/**
 * Tool definitions for DOCX XML-based editing capabilities.
 *
 * These tools let the AI unpack a DOCX binary, read/edit any XML file
 * in the archive, and repack into a fresh .docx binary.
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

export const UNPACK_DOCUMENT_TOOL: Tool = {
  name: 'unpack_document',
  description: `Unpack a DOCX file for XML-based editing. Returns the list of XML files in the archive.

Call this first before using read_document_xml or edit_document_xml. Works on uploaded documents and AI-generated ones.

The main content file is word/document.xml. Other important files:
- word/styles.xml — document styles
- word/numbering.xml — list numbering definitions
- word/comments.xml — comments
- word/_rels/document.xml.rels — relationships (images, hyperlinks)
- [Content_Types].xml — content type declarations`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document to unpack',
      },
    },
    required: ['documentId'],
  },
};

export const READ_DOCUMENT_XML_TOOL: Tool = {
  name: 'read_document_xml',
  description: `Read an XML file from the unpacked DOCX archive. Returns pretty-printed XML with smart quotes escaped.

Use after unpack_document. Common files to read:
- word/document.xml — main document content (paragraphs, tables, images)
- word/styles.xml — style definitions
- word/comments.xml — document comments
- word/numbering.xml — list numbering config`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      filePath: {
        type: 'string',
        description: 'Path of the file within the DOCX archive (e.g. "word/document.xml")',
      },
    },
    required: ['documentId', 'filePath'],
  },
};

export const READ_DOCUMENT_OUTLINE_TOOL: Tool = {
  name: 'read_document_outline',
  description: `Read a compact outline of a DOCX document from heading paragraphs only.

Use this before broad restructuring tasks to find sections without loading raw XML. Returns heading paragraph IDs, levels, text, hashes, and safety flags.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      filePath: {
        type: 'string',
        description: 'Optional path within the DOCX archive. Defaults to "word/document.xml".',
      },
    },
    required: ['documentId'],
  },
};

export const READ_DOCUMENT_TEXT_TOOL: Tool = {
  name: 'read_document_text',
  description: `Read DOCX content as compact visible text with paragraph IDs and edit-safety metadata.

This is the preferred read tool for Word editing. It returns visible text, not raw OOXML. It hides field instructions and shows field results, so generated TOC entries appear as the user sees them.

Use startParagraph/endParagraph to read in chunks. Without a range, the tool returns the first chunk. Use query to find matching paragraphs.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      filePath: {
        type: 'string',
        description: 'Optional path within the DOCX archive. Defaults to "word/document.xml".',
      },
      startParagraph: {
        type: 'number',
        description: '1-based paragraph index to start from. Defaults to 1.',
      },
      endParagraph: {
        type: 'number',
        description: 'Inclusive 1-based paragraph index to end at. The tool caps each call to 250 paragraphs.',
      },
      query: {
        type: 'string',
        description: 'Optional visible-text search query. When provided, returns matching paragraphs instead of a numeric range.',
      },
      includeEmpty: {
        type: 'boolean',
        description: 'Whether to include empty paragraphs. Defaults to false.',
      },
      maxParagraphs: {
        type: 'number',
        description: 'Maximum paragraphs to return, capped at 250. Defaults to 120.',
      },
    },
    required: ['documentId'],
  },
};

export const INSPECT_DOCUMENT_PARAGRAPH_TOOL: Tool = {
  name: 'inspect_document_paragraph',
  description: `Inspect one DOCX paragraph as a lightweight segment model.

Use this when read_document_text marks a paragraph as complex, generated, media, or anchored and you need to understand its internal structure without reading raw XML. Returns text segments, bookmarks, fields, tabs, images, and allowed operations.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      paragraphId: {
        type: 'string',
        description: 'Paragraph ID from read_document_text or read_document_outline',
      },
      filePath: {
        type: 'string',
        description: 'Optional path within the DOCX archive. Defaults to "word/document.xml".',
      },
    },
    required: ['documentId', 'paragraphId'],
  },
};

export const REPLACE_DOCUMENT_TEXT_TOOL: Tool = {
  name: 'replace_document_text',
  description: `Safely replace visible text inside one DOCX paragraph.

Use this for language polishing and narrow edits. The tool mutates only visible w:t text nodes and preserves surrounding OOXML. It refuses hard structural paragraphs such as fields, comments, footnotes, hyperlinks, tracked changes, equations, or content controls.

Pass expectedHash from read_document_text/inspect_document_paragraph when available to avoid editing stale paragraph state.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      paragraphId: {
        type: 'string',
        description: 'Paragraph ID from read_document_text',
      },
      oldText: {
        type: 'string',
        description: 'Exact visible text to replace within the paragraph',
      },
      newText: {
        type: 'string',
        description: 'Replacement visible text',
      },
      occurrence: {
        type: 'number',
        description: '1-based occurrence when oldText appears multiple times. Defaults to 1.',
      },
      expectedHash: {
        type: 'string',
        description: 'Optional paragraph text hash from the latest read. The edit is rejected if the paragraph changed.',
      },
      markRed: {
        type: 'boolean',
        description: 'Color the changed text run red. Defaults to false.',
      },
      filePath: {
        type: 'string',
        description: 'Optional path within the DOCX archive. Defaults to "word/document.xml".',
      },
    },
    required: ['documentId', 'paragraphId', 'oldText', 'newText'],
  },
};

export const REWRITE_DOCUMENT_PARAGRAPH_TOOL: Tool = {
  name: 'rewrite_document_paragraph',
  description: `Rewrite a strictly simple DOCX paragraph.

This tool is intentionally conservative. It only works on paragraphs marked rewrite=yes by read_document_text. For mixed-runs, anchored, generated, media, or complex paragraphs, use replace_document_text for narrow edits or inspect_document_paragraph before falling back to XML.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      paragraphId: {
        type: 'string',
        description: 'Paragraph ID from read_document_text',
      },
      newText: {
        type: 'string',
        description: 'Complete replacement visible text',
      },
      expectedHash: {
        type: 'string',
        description: 'Optional paragraph text hash from the latest read. The edit is rejected if the paragraph changed.',
      },
      markRed: {
        type: 'boolean',
        description: 'Color the rewritten paragraph text red. Defaults to false.',
      },
      filePath: {
        type: 'string',
        description: 'Optional path within the DOCX archive. Defaults to "word/document.xml".',
      },
    },
    required: ['documentId', 'paragraphId', 'newText'],
  },
};

export const MARK_DOCUMENT_FIELDS_DIRTY_TOOL: Tool = {
  name: 'mark_document_fields_dirty',
  description: `Mark DOCX fields as dirty so Word refreshes generated fields such as tables of contents and page references when the document opens.

Use after heading edits, section moves, or other changes that could affect the TOC or cross references. Then call repack_document.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
    },
    required: ['documentId'],
  },
};

export const MOVE_DOCUMENT_SECTION_TOOL: Tool = {
  name: 'move_document_section',
  description: `Move a whole DOCX section before another heading, using heading paragraph IDs from read_document_outline.

The tool computes the OOXML range internally: from sectionHeadingId through the paragraph before the next same-or-higher heading. It moves the original XML nodes, preserving formatting, bookmarks, tables, images, and fields. Use this instead of reading XML for section reordering.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: { type: 'string', description: 'ID of the document' },
      sectionHeadingId: { type: 'string', description: 'Heading paragraph ID of the section to move' },
      beforeHeadingId: { type: 'string', description: 'Heading paragraph ID to insert before' },
      filePath: { type: 'string', description: 'Optional path. Defaults to "word/document.xml".' },
    },
    required: ['documentId', 'sectionHeadingId', 'beforeHeadingId'],
  },
};

export const DELETE_DOCUMENT_SECTION_TOOL: Tool = {
  name: 'delete_document_section',
  description: `Delete a whole DOCX section by heading paragraph ID.

The section range starts at sectionHeadingId and ends before the next same-or-higher heading. Use for removing duplicate sections or obsolete subsections without reading raw XML. Large or suspicious section deletes are refused unless allowLargeDelete=true after confirming the section range from the outline/text view.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: { type: 'string', description: 'ID of the document' },
      sectionHeadingId: { type: 'string', description: 'Heading paragraph ID of the section to delete' },
      allowLargeDelete: {
        type: 'boolean',
        description: 'Set true only after confirming the target is a real large section, not a standalone instruction/meta-note paragraph.',
      },
      filePath: { type: 'string', description: 'Optional path. Defaults to "word/document.xml".' },
    },
    required: ['documentId', 'sectionHeadingId'],
  },
};

export const DELETE_DOCUMENT_BLOCKS_TOOL: Tool = {
  name: 'delete_document_blocks',
  description: `Delete specific top-level DOCX paragraph blocks by paragraph ID.

Use for removing standalone meta/instruction paragraphs such as "根据修改建议..." notes. This does not delete paragraphs inside tables.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: { type: 'string', description: 'ID of the document' },
      paragraphIds: {
        type: 'array',
        description: 'Paragraph IDs to delete',
        items: { type: 'string' },
      },
      filePath: { type: 'string', description: 'Optional path. Defaults to "word/document.xml".' },
    },
    required: ['documentId', 'paragraphIds'],
  },
};

export const RENAME_DOCUMENT_HEADING_TOOL: Tool = {
  name: 'rename_document_heading',
  description: `Rename a DOCX heading by paragraph ID while preserving heading style, bookmarks, and surrounding structure.

Use heading IDs from read_document_outline. Set markRed=true when the user asks changed text to be red.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: { type: 'string', description: 'ID of the document' },
      paragraphId: { type: 'string', description: 'Heading paragraph ID' },
      newText: { type: 'string', description: 'New visible heading text' },
      expectedHash: { type: 'string', description: 'Optional heading text hash from the latest read' },
      markRed: { type: 'boolean', description: 'Color the changed heading text red. Defaults to false.' },
      filePath: { type: 'string', description: 'Optional path. Defaults to "word/document.xml".' },
    },
    required: ['documentId', 'paragraphId', 'newText'],
  },
};

export const INSERT_DOCUMENT_PARAGRAPH_TOOL: Tool = {
  name: 'insert_document_paragraph_after',
  description: `Insert a new paragraph after an existing DOCX paragraph ID.

Use for adding bridge analysis, expanded method explanation, or image-placement notes. Set markRed=true when the user asks changed text to be red.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: { type: 'string', description: 'ID of the document' },
      afterParagraphId: { type: 'string', description: 'Paragraph ID to insert after' },
      text: { type: 'string', description: 'Visible paragraph text to insert' },
      styleSource: {
        type: 'string',
        enum: ['after', 'normal'],
        description: 'Use the previous paragraph style or a plain normal paragraph. Defaults to normal.',
      },
      markRed: { type: 'boolean', description: 'Color inserted text red. Defaults to false.' },
      filePath: { type: 'string', description: 'Optional path. Defaults to "word/document.xml".' },
    },
    required: ['documentId', 'afterParagraphId', 'text'],
  },
};

export const APPLY_DOCUMENT_OPERATIONS_TOOL: Tool = {
  name: 'apply_document_operations',
  description: `Apply a batch of semantic DOCX edit operations over paragraph/heading IDs.

Use this for a clean edit path after read_document_outline/read_document_text. Operations are applied in order. Supported operation types: move_section, delete_section, delete_blocks, rename_heading, replace_text, rewrite_paragraph, insert_paragraph_after, mark_fields_dirty. Prefer this over reading raw XML for structural thesis edits. For delete_section, include allowLargeDelete=true only after confirming the range is a real large section.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: { type: 'string', description: 'ID of the document' },
      operations: {
        type: 'array',
        description: 'Ordered semantic edit operations. Each item must include a type field.',
        items: {
          type: 'object',
          additionalProperties: true,
          properties: {
            type: {
              type: 'string',
              enum: [
                'move_section',
                'delete_section',
                'delete_blocks',
                'rename_heading',
                'replace_text',
                'rewrite_paragraph',
                'insert_paragraph_after',
                'mark_fields_dirty',
              ],
            },
          },
          required: ['type'],
        },
      },
    },
    required: ['documentId', 'operations'],
  },
};

export const EDIT_DOCUMENT_XML_TOOL: Tool = {
  name: 'edit_document_xml',
  description: `Replace an XML file's entire content in the unpacked DOCX archive. The XML must be valid and well-formed.

After editing, call repack_document to save changes. Common edits:
- Find/replace text in word/document.xml
- Add tracked changes (w:ins/w:del) for review
- Insert comments (update both word/document.xml and word/comments.xml)
- Modify formatting via w:rPr (run properties) and w:pPr (paragraph properties)`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      filePath: {
        type: 'string',
        description: 'Path of the file within the DOCX archive (e.g. "word/document.xml")',
      },
      newXml: {
        type: 'string',
        description: 'Complete replacement XML for the file',
      },
    },
    required: ['documentId', 'filePath', 'newXml'],
  },
};

export const PATCH_DOCUMENT_XML_TOOL: Tool = {
  name: 'patch_document_xml',
  description: `Apply targeted search/replace edits to an XML file in the unpacked DOCX archive. Much more efficient than edit_document_xml for small changes (text edits, formatting tweaks).

Rules:
- Copy search strings exactly from read_document_xml output (including indentation)
- Each search must match exactly once (include surrounding tags for uniqueness)
- Edits are applied sequentially — earlier edits can affect later matches
- Call repack_document after to save changes`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      filePath: {
        type: 'string',
        description: 'Path of the file within the DOCX archive (e.g. "word/document.xml")',
      },
      edits: {
        type: 'array',
        description: 'Ordered search/replace pairs applied sequentially',
        items: {
          type: 'object',
          properties: {
            search: { type: 'string', description: 'Exact substring to find in the XML' },
            replace: { type: 'string', description: 'Replacement string' },
            occurrence: { type: 'number', description: '1-based occurrence to target when the search string appears multiple times. Default: 1 (first match). Omit if the search is unique.' },
          },
          required: ['search', 'replace'],
        },
      },
    },
    required: ['documentId', 'filePath', 'edits'],
  },
};

export const READ_DOCUMENT_IMAGE_TOOL: Tool = {
  name: 'read_document_image',
  description: `Read an embedded image from an uploaded document. Returns the image for visual analysis.

Use this when you see image references like "attachment:image1.png" in the document content and need to view or analyze the actual image.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document',
      },
      imageName: {
        type: 'string',
        description: 'Image filename from the attachment reference (e.g. "image1.png")',
      },
    },
    required: ['documentId', 'imageName'],
  },
};

export const REPACK_DOCUMENT_TOOL: Tool = {
  name: 'repack_document',
  description: `Condense XML and repack into a DOCX binary. Stores the new binary for download.

Call this after making changes with edit_document_xml. The user can then download the edited document.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      documentId: {
        type: 'string',
        description: 'ID of the document to repack',
      },
    },
    required: ['documentId'],
  },
};
